/*
 * rheincool — Warenkorb-Updates (Warenkorb-Handoff §4, §11, §12)
 * Event-Delegation auf Drawer UND Cart-Seite:
 * - Mengen-Stepper: Bundle-Zeilen aktualisieren Gerät UND Montage im selben Request
 * - Gerät entfernen bei Bundle: Bestätigungsdialog, Montage geht mit
 * - „Montage entfernen": nur die Montage-Position (Textlink, kein ✕)
 * - Undo-Toast nach Entfernen (6 s), stellt Positionen inkl. Properties wieder her
 * - Für später speichern: localStorage-Merkliste, gerendert in [data-saved-for-later]
 * - Terminwunsch: Line-Item-Properties an der Montage-Position (/cart/change.js)
 * - Gutschein: /discount/CODE-Redirect (Anrechnung im Checkout)
 */
(() => {
  'use strict';

  // Das Script wird von Cart-Section UND Drawer-Snippet eingebunden —
  // doppelte Listener würden jeden Klick zweimal feuern (zweiter Request
  // läuft mit verbrauchtem Line-Key auf einen Fehler).
  if (window.__rcCartBundle) return;
  window.__rcCartBundle = true;

  let busy = false;
  const track = (event, params) => {
    if (window.rcTrack) window.rcTrack(event, params || {});
  };

  /* ---------- Section-Rendering ---------- */

  const collectSectionIds = () => {
    const ids = new Set(['cart-icon-bubble']);
    document.querySelectorAll('[data-cart-section]').forEach((el) => {
      ids.add(el.dataset.cartSection);
    });
    return Array.from(ids);
  };

  const applySections = (sections) => {
    if (!sections) return;
    document.querySelectorAll('[data-cart-section]').forEach((el) => {
      const html = sections[el.dataset.cartSection];
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector(`[data-cart-section="${el.dataset.cartSection}"]`);
      if (fresh) el.innerHTML = fresh.innerHTML;
    });
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      bubble.innerHTML = new DOMParser()
        .parseFromString(sections['cart-icon-bubble'], 'text/html')
        .body.innerHTML;
    }
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      const hasItems = drawer.querySelector('.rc-line');
      drawer.classList.toggle('is-empty', !hasItems);
    }
    renderSaved();
  };

  const request = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || 'cart_request_failed');
    return data;
  };

  const update = async (updates, lineEl) => {
    if (busy) return;
    busy = true;
    if (lineEl) lineEl.classList.add('is-updating');
    try {
      const data = await request(`${window.routes.cart_update_url}.js`, {
        updates,
        sections: collectSectionIds().join(','),
        sections_url: window.location.pathname,
      });
      applySections(data.sections);
    } catch (error) {
      console.warn('[rc-cart]', error);
      window.location.reload();
    } finally {
      busy = false;
    }
  };

  const buildUpdates = (lineEl, quantity) => {
    const updates = { [lineEl.dataset.lineKey]: quantity };
    // Bundle: Montage-Position folgt der Gerätemenge (HANDOFF §7)
    if (lineEl.dataset.partnerKey) {
      updates[lineEl.dataset.partnerKey] = quantity;
    }
    return updates;
  };

  /* ---------- Zeilen-Snapshot für Undo / Merkliste ---------- */

  const snapshotLine = async (lineEl) => {
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const keys = [lineEl.dataset.lineKey, lineEl.dataset.partnerKey].filter(Boolean);
      return cart.items
        .filter((item) => keys.includes(item.key))
        .map((item) => ({
          id: item.variant_id,
          quantity: item.quantity,
          properties: item.properties || {},
          title: item.product_title,
          price: item.final_line_price,
          image: item.image,
          url: item.url,
        }));
    } catch {
      return [];
    }
  };

  const refreshSections = async () => {
    // Kein Accept-Header: mit application/json antwortet /cart mit dem
    // Cart-JSON statt der Section-Rendering-Antwort
    const response = await fetch(`${window.location.pathname}?sections=${collectSectionIds().join(',')}`);
    if (response.ok) applySections(await response.json());
  };

  const readdLines = async (lines) => {
    for (const line of lines) {
      await request('/cart/add.js', {
        items: [{ id: line.id, quantity: line.quantity, properties: line.properties }],
      });
    }
    await refreshSections();
  };

  /* ---------- Undo-Toast (6 s) ---------- */

  let toastTimer = null;
  let lastRemoved = null;

  const showToast = (text) => {
    const toast = document.querySelector('[data-cart-toast]');
    if (!toast) return;
    toast.querySelector('[data-cart-toast-text]').textContent = text;
    toast.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toast.hidden = true;
      lastRemoved = null;
    }, 6000);
  };

  const removeLine = async (lineEl, montageOnly) => {
    const lines = await snapshotLine(lineEl);
    if (montageOnly) {
      lastRemoved = lines.filter((l) => l.properties && l.properties._parent);
      await update({ [lineEl.dataset.partnerKey]: 0 }, lineEl);
      track('montage_remove', { handle: lineEl.dataset.lineHandle });
    } else {
      lastRemoved = lines;
      await update(buildUpdates(lineEl, 0), lineEl);
      track('cart_remove', { handle: lineEl.dataset.lineHandle, war_bundle: Boolean(lineEl.dataset.partnerKey) });
    }
    const toastText = document.querySelector('[data-cart-toast]')?.dataset.toastText || 'Artikel entfernt';
    if (lastRemoved && lastRemoved.length) showToast(toastText);
  };

  /* ---------- Merkliste (localStorage, Warenkorb-Handoff §4.3) ---------- */

  const SAVED_KEY = 'rc-saved-for-later';

  const readSaved = () => {
    try {
      return JSON.parse(localStorage.getItem(SAVED_KEY)) || [];
    } catch {
      return [];
    }
  };

  const writeSaved = (list) => {
    localStorage.setItem(SAVED_KEY, JSON.stringify(list.slice(0, 20)));
    renderSaved();
  };

  const money = (cents) => `${(cents / 100).toFixed(2).replace('.', ',').replace(/,00$/, ',–')} €`;

  const renderSaved = () => {
    const saved = readSaved();
    document.querySelectorAll('[data-saved-for-later]').forEach((box) => {
      const list = box.querySelector('[data-saved-list]');
      const count = box.querySelector('[data-saved-count]');
      box.hidden = saved.length === 0;
      if (count) count.textContent = saved.length ? `(${saved.length})` : '';
      if (!list) return;
      list.innerHTML = '';
      saved.forEach((entry, index) => {
        const li = document.createElement('li');
        li.className = 'rc-cart__saved-item';
        const img = entry.image
          ? `<img src="${entry.image}&width=120" alt="" width="60" height="48" loading="lazy">`
          : '';
        li.innerHTML = `
          <a href="${entry.url}" class="rc-cart__saved-media">${img}</a>
          <span class="rc-cart__saved-info">
            <a href="${entry.url}" class="rc-cart__saved-title"></a>
            <span class="rc-cart__saved-price rc-num">${money(entry.price)}</span>
          </span>
          <button type="button" class="rc-btn-outline rc-cart__saved-add" data-saved-index="${index}"></button>
        `;
        li.querySelector('.rc-cart__saved-title').textContent = entry.title;
        li.querySelector('[data-saved-index]').textContent = li.closest('[data-saved-for-later]')?.dataset.savedCta || 'In den Warenkorb';
        list.appendChild(li);
      });
    });
  };

  const saveForLater = async (lineEl) => {
    const lines = await snapshotLine(lineEl);
    const device = lines.find((l) => !(l.properties && l.properties._parent));
    if (!device) return;
    const saved = readSaved();
    saved.unshift({ id: device.id, title: device.title, price: device.price, image: device.image, url: device.url });
    writeSaved(saved);
    await update(buildUpdates(lineEl, 0), lineEl);
    track('save_for_later', { handle: lineEl.dataset.lineHandle });
  };

  const moveToCart = async (index) => {
    const saved = readSaved();
    const entry = saved[index];
    if (!entry) return;
    saved.splice(index, 1);
    writeSaved(saved);
    await readdLines([{ id: entry.id, quantity: 1, properties: {} }]).catch(() => window.location.reload());
    track('move_to_cart', { handle: entry.url });
  };

  /* ---------- Terminwunsch → Line-Item-Properties ---------- */

  const setAppointment = async (box) => {
    const key = box.dataset.appointmentKey;
    const date = box.querySelector('[data-appointment-date]')?.value || '';
    const time = box.querySelector('.rc-line__appointment-time.is-selected')?.dataset.appointmentTime || '';
    try {
      // Bestehende Properties (_bundle_id, _parent …) erhalten — change.js ersetzt sonst alles
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      const item = cart.items.find((i) => i.key === key);
      if (!item) return;
      const properties = { ...(item.properties || {}) };
      if (date) properties['Wunschtermin ab'] = date;
      else delete properties['Wunschtermin ab'];
      if (time) properties.Tageszeit = time;
      else delete properties.Tageszeit;
      const data = await request('/cart/change.js', { id: key, quantity: item.quantity, properties });
      // Property-Änderung erzeugt einen neuen Line-Key — DOM-Referenzen nachziehen
      const fresh = (data.items || []).find(
        (i) => i.variant_id === item.variant_id && i.properties && i.properties._parent
          && i.properties._bundle_id === (item.properties || {})._bundle_id
      );
      if (fresh) {
        box.dataset.appointmentKey = fresh.key;
        const line = box.closest('[data-line-key]');
        if (line) line.dataset.partnerKey = fresh.key;
      }
      track('appointment_preference_set', { datum_ab: date, tageszeit: time });
    } catch (error) {
      console.warn('[rc-cart] Terminwunsch', error);
    }
  };

  /* ---------- Events ---------- */

  document.addEventListener('click', (event) => {
    const savedBtn = event.target.closest('[data-saved-index]');
    if (savedBtn) {
      moveToCart(parseInt(savedBtn.dataset.savedIndex, 10));
      return;
    }
    if (event.target.closest('[data-cart-undo]')) {
      const toast = document.querySelector('[data-cart-toast]');
      if (toast) toast.hidden = true;
      if (lastRemoved && lastRemoved.length) {
        readdLines(lastRemoved).catch(() => window.location.reload());
        track('cart_undo_remove', {});
        lastRemoved = null;
      }
      return;
    }

    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    const input = line.querySelector('[data-cart-qty-input]');
    const current = parseInt(input?.value || '1', 10);

    if (event.target.closest('[data-cart-qty-plus]')) {
      update(buildUpdates(line, Math.min(10, current + 1)), line);
      track('cart_quantity_change', { handle: line.dataset.lineHandle, alt: current, neu: current + 1 });
    } else if (event.target.closest('[data-cart-qty-minus]')) {
      update(buildUpdates(line, Math.max(0, current - 1)), line);
      track('cart_quantity_change', { handle: line.dataset.lineHandle, alt: current, neu: current - 1 });
    } else if (event.target.closest('[data-montage-remove]')) {
      removeLine(line, true);
    } else if (event.target.closest('[data-cart-save-later]')) {
      saveForLater(line);
    } else if (event.target.closest('[data-cart-remove]')) {
      // Bundle: Gerät entfernen nimmt die Montage mit — mit Dialog (§11)
      if (line.dataset.partnerKey && line.dataset.bundleConfirm) {
        if (!window.confirm(line.dataset.bundleConfirm)) return;
      }
      removeLine(line, false);
    } else if (event.target.closest('[data-appointment-time]')) {
      const btn = event.target.closest('[data-appointment-time]');
      const group = btn.closest('[data-appointment]');
      group.querySelectorAll('[data-appointment-time]').forEach((b) => {
        b.classList.toggle('is-selected', b === btn);
        b.setAttribute('aria-checked', b === btn ? 'true' : 'false');
      });
      setAppointment(group);
    }
  });

  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-appointment-date]')) {
      setAppointment(event.target.closest('[data-appointment]'));
      return;
    }
    if (!event.target.matches('[data-cart-qty-input]')) return;
    const line = event.target.closest('[data-line-key]');
    if (!line) return;
    const quantity = Math.min(10, Math.max(0, parseInt(event.target.value || '0', 10)));
    update(buildUpdates(line, quantity), line);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('[data-coupon-form]');
    if (!form) return;
    event.preventDefault();
    const code = form.querySelector('[data-coupon-input]')?.value.trim();
    if (!code) return;
    track('coupon_apply', { code });
    // Nativer Shopify-Weg ohne App: Code als Discount-Cookie, angerechnet im Checkout
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
  });

  document.addEventListener('DOMContentLoaded', () => {
    renderSaved();
    const view = document.querySelector('[data-rc-track-view="view_cart"]');
    if (view) {
      track('view_cart', {
        positionen: document.querySelectorAll('.rc-line').length,
        hat_montage: view.dataset.rcTrackMontage === 'true',
      });
    }
  });
})();
